import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Buildings, CurrencyInr, Trash, Plus, ArrowRight, ClockClockwise,
  WarningCircle, Ruler,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { istToday } from "@/lib/istDate";

/**
 * FabricationPanel — shown inside OrderDetailDialog for gate/railing orders.
 *
 * Phase-1 model (2026-02-15):
 *   ONE customer order can be split across multiple fabricators.
 *   Each ACTIVE assignment carries assigned_sqft × labour_rate = job_value.
 *   Reassigning to a different fabricator preserves prior payment history
 *   (old assignment is marked `status="reassigned"`, not deleted).
 *
 *   Owner UI:
 *     - Assignment summary strip (customer sqft · assigned · remaining)
 *     - Assignment cards per active fabricator with paid/due
 *     - "Assign Fabricator" form (sqft + rate + optional advance + deadline)
 *     - "PAY ADVANCE" flow to log additional payments (unchanged)
 */
export default function FabricationPanel({ order, onChanged }) {
  const [fabricators, setFabricators] = useState([]);
  const [modes, setModes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payments, setPayments] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [showPayForm, setShowPayForm] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [deadline, setDeadline] = useState(order.deadline_date || "");

  // Assignment form state
  const [selectedId, setSelectedId] = useState("");
  const [aSqft, setASqft] = useState("");
  const [aRate, setARate] = useState("");
  const [aDeadline, setADeadline] = useState("");
  const [aAdvance, setAAdvance] = useState("");
  const [aAdvMode, setAAdvMode] = useState("cash");
  const [aAdvDate, setAAdvDate] = useState(() => istToday());
  const [aAdvNote, setAAdvNote] = useState("");
  const [aOverride, setAOverride] = useState(false);

  // Pay-advance-only form state (for existing fabricator)
  const [amt, setAmt] = useState("");
  const [mode, setMode] = useState("cash");
  const [note, setNote] = useState("");
  const [paidOn, setPaidOn] = useState(() => istToday());

  useEffect(() => { setDeadline(order.deadline_date || ""); }, [order.deadline_date]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: fabs }, { data: m }] = await Promise.all([
          api.get("/fabricators"),
          api.get("/settings/payment-modes"),
        ]);
        setFabricators(fabs || []);
        setModes(m || []);
        if (m?.length) {
          setMode(m[0].key);
          setAAdvMode(m[0].key);
        }
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const loadSummary = async () => {
    if (!order.id) return;
    try {
      const { data } = await api.get(`/orders/${order.id}/assignments`);
      setSummary(data);
    } catch (_e) { /* ignore */ }
  };

  const loadPayments = async () => {
    if (!order.id) return;
    try {
      const { data } = await api.get(`/orders/${order.id}/fabricator-payments`);
      setPayments(data.payments || []);
      setTotalPaid(Number(data.total || 0));
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    loadSummary();
    loadPayments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id, order.fabricator_id]);

  const activeAssignments = useMemo(
    () => (summary?.assignments || []).filter((a) => (a.status || "active") === "active"),
    [summary]
  );
  const currentFabricator = useMemo(
    () => fabricators.find((f) => f.id === order.fabricator_id) || null,
    [fabricators, order.fabricator_id]
  );
  const remaining = Number(summary?.remaining_sqft ?? 0);
  const customerSqft = Number(summary?.customer_sqft ?? 0);
  const totalAssigned = Number(summary?.total_assigned_sqft ?? 0);
  const overRequested = Number(aSqft || 0) > remaining + 0.005;
  const jobValue = (Number(aSqft || 0) * Number(aRate || 0)) || 0;
  const dueAfterAdv = Math.max(jobValue - (Number(aAdvance || 0) || 0), 0);

  const openAssignForm = () => {
    setSelectedId("");
    // Pre-fill sqft with remaining (or full customer sqft if nothing assigned)
    setASqft(String(Math.max(remaining, 0)));
    setARate("");
    setADeadline(order.deadline_date || "");
    setAAdvance("");
    setAAdvNote("");
    setAOverride(false);
    setShowAssignForm(true);
  };

  const submitAssignment = async (e) => {
    e.preventDefault();
    if (!selectedId) { toast.error("Fabricator select karo"); return; }
    if (!(Number(aSqft) > 0)) { toast.error("Assigned SqFt daaliye"); return; }
    if (!(Number(aRate) >= 0)) { toast.error("Rate daaliye"); return; }
    if (Number(aAdvance) > 0 && !aAdvMode) { toast.error("Advance ke liye mode chuno"); return; }

    setBusy(true);
    try {
      const payload = {
        fabricator_id: selectedId,
        assigned_sqft: Number(aSqft),
        labour_rate_per_sqft: Number(aRate),
        deadline_date: aDeadline || null,
        advance_amount: Number(aAdvance) || 0,
        advance_mode: aAdvMode,
        advance_paid_on: aAdvDate,
        advance_note: aAdvNote,
        override_over_assignment: aOverride,
      };
      await api.post(`/orders/${order.id}/assign-fabricator`, payload);
      toast.success("Fabricator assigned");
      setShowAssignForm(false);
      onChanged?.();
      loadSummary();
      loadPayments();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Server returns a structured payload for over_assignment
      if (detail && typeof detail === "object" && detail.code === "over_assignment") {
        toast.error(detail.message || "Over-assignment blocked");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setBusy(false); }
  };

  const unassign = async () => {
    if (!window.confirm("Fabricator hata dein? Payment history preserved rahegi.")) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/assign-fabricator`, {
        fabricator_id: null,
        reassignment_reason: "unassigned by admin",
      });
      toast.success("Fabricator removed");
      onChanged?.();
      loadSummary();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const n = Number(amt) || 0;
    if (n <= 0) { toast.error("Amount valid daaliye"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/fabricator-payment`, {
        amount: n, mode, note, paid_on: paidOn,
      });
      toast.success(`₹${n} advance logged to ${currentFabricator?.name || "fabricator"}`);
      setAmt(""); setNote(""); setShowPayForm(false);
      loadPayments();
      loadSummary();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const removePayment = async (id) => {
    if (!window.confirm("Ye advance entry delete karein?")) return;
    try {
      await api.delete(`/fabricator-payments/${id}`);
      toast.success("Entry deleted");
      loadPayments();
      loadSummary();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveDeadline = async () => {
    if ((deadline || null) === (order.deadline_date || null)) return;
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/deadline`, { deadline_date: deadline || null });
      toast.success(deadline ? "Deadline updated" : "Deadline cleared");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  const sqft = (n) => `${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} sqft`;

  return (
    <div className="surface p-4 rounded-sm border-l-4 border-cyan-500/60" data-testid="fabrication-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs font-mono-num tracking-widest text-slate-400">
          <Buildings size={14} className="text-cyan-300" weight="fill" />
          // FABRICATION DEPARTMENT
        </div>
        {totalPaid > 0 && (
          <div className="text-[10px] font-mono-num text-amber-300">
            TOTAL PAID: <span className="font-bold">{fmt(totalPaid)}</span>
          </div>
        )}
      </div>

      {/* SqFt allocation strip */}
      {summary && (
        <div
          className="mt-3 grid grid-cols-3 gap-2 bg-slate-800/40 rounded-sm p-3 text-center"
          data-testid="fab-alloc-strip"
        >
          <StatChip label="CUSTOMER SQFT" value={sqft(customerSqft)} tone="cyan"
                    testid="fab-customer-sqft" />
          <StatChip label="ASSIGNED" value={sqft(totalAssigned)} tone="amber"
                    testid="fab-assigned-sqft" />
          <StatChip
            label="REMAINING"
            value={sqft(remaining)}
            tone={remaining < -0.005 ? "red" : remaining > 0.005 ? "emerald" : "slate"}
            testid="fab-remaining-sqft"
          />
        </div>
      )}

      {/* Internal deadline — admin+fabrication only, hidden from customer */}
      <div className="mt-3 flex flex-col md:flex-row md:items-end gap-2 bg-amber-500/5 border border-amber-500/30 rounded-sm p-3" data-testid="fab-deadline-block">
        <div className="flex-1">
          <div className="text-[10px] font-mono-num tracking-widest text-amber-300 flex items-center gap-1">
            🔒 INTERNAL DEADLINE
            <span className="text-slate-500 normal-case font-sans text-[10px] tracking-normal ml-1">(customer nahi dekhta)</span>
          </div>
          <input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="bg-slate-800/60 border border-amber-500/40 text-amber-100 h-9 rounded-sm mt-1 font-mono-num px-2 w-full md:max-w-xs"
            data-testid="fab-deadline-input"
          />
        </div>
        <Button
          onClick={saveDeadline}
          disabled={busy || (deadline || "") === (order.deadline_date || "")}
          variant="outline"
          className="rounded-sm h-9 border-amber-500/60 text-amber-300 hover:bg-amber-500/10 text-xs font-heading tracking-widest"
          data-testid="fab-deadline-save"
        >
          {deadline ? "SAVE DEADLINE" : (order.deadline_date ? "CLEAR" : "SET")}
        </Button>
      </div>

      {/* Active assignments (Phase 1) */}
      {activeAssignments.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="fab-active-list">
          {activeAssignments.map((a, idx) => (
            <ActiveAssignmentCard
              key={a.id || idx}
              a={a}
              fmt={fmt}
              sqft={sqft}
              testid={`fab-active-${idx}`}
              onPayAdvance={() => setShowPayForm(true)}
              onUnassign={
                a.fabricator_id === order.fabricator_id ? unassign : null
              }
            />
          ))}
        </div>
      )}

      {/* Assign new fabricator button (if remaining > 0) or reassign */}
      <div className="mt-3 flex gap-2 flex-wrap">
        <Button
          onClick={openAssignForm}
          variant="outline"
          className="rounded-sm h-10 border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10 text-xs font-heading tracking-widest"
          data-testid="fab-open-assign-btn"
        >
          <Plus size={14} className="mr-1" />
          {activeAssignments.length ? "ASSIGN ANOTHER" : "ASSIGN FABRICATOR"}
        </Button>
      </div>

      {fabricators.length === 0 && (
        <div className="text-[11px] text-amber-300 mt-2">
          Koi fabricator user nahi mila. <b>Settings → Users</b> me role <b>"fabrication"</b> se user add karo.
        </div>
      )}

      {/* V2 assignment form */}
      {showAssignForm && (
        <form
          onSubmit={submitAssignment}
          className="mt-3 bg-slate-800/50 border border-cyan-500/40 rounded-sm p-3 space-y-3"
          data-testid="fab-assign-form"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono-num tracking-widest text-cyan-300 flex items-center gap-1">
              <Ruler size={12} weight="bold" /> // NEW ASSIGNMENT
            </div>
            <button
              type="button"
              onClick={() => setShowAssignForm(false)}
              className="text-slate-500 hover:text-red-400 text-xs"
              data-testid="fab-assign-close"
            >
              ✕ CLOSE
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">FABRICATOR *</Label>
              <Select value={selectedId || undefined} onValueChange={setSelectedId}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1" data-testid="fab-assign-select">
                  <SelectValue placeholder="Fabricator chuno…" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {fabricators.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name} · {f.email || f.mobile || "—"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DEADLINE (internal)</Label>
              <input
                type="date"
                value={aDeadline}
                onChange={(e) => setADeadline(e.target.value)}
                className="bg-slate-800/60 border border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num px-2 w-full"
                data-testid="fab-assign-deadline"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">
                ASSIGNED SQFT * <span className="text-slate-500 normal-case">(remaining {sqft(remaining)})</span>
              </Label>
              <Input
                type="number" min="0" step="0.01"
                value={aSqft} onChange={(e) => setASqft(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num"
                data-testid="fab-assign-sqft"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">LABOUR ₹/SQFT *</Label>
              <Input
                type="number" min="0" step="0.01"
                value={aRate} onChange={(e) => setARate(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num"
                data-testid="fab-assign-rate"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">FABRICATOR JOB VALUE</Label>
              <div
                className="bg-slate-900/60 border border-emerald-500/40 text-emerald-300 h-10 rounded-sm mt-1 font-mono-num font-bold px-3 flex items-center"
                data-testid="fab-assign-job-value"
              >
                {fmt(jobValue)}
              </div>
            </div>
          </div>

          {overRequested && (
            <div
              className="flex items-start gap-2 p-2 bg-red-500/10 border border-red-500/40 rounded-sm text-red-300 text-xs"
              data-testid="fab-assign-over-warning"
            >
              <WarningCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold">Over-assignment warning</div>
                Assigned {sqft(Number(aSqft))} &gt; available {sqft(remaining)}. Owner
                override chahiye toh niche checkbox tick karo.
                <label className="flex items-center gap-1 mt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aOverride}
                    onChange={(e) => setAOverride(e.target.checked)}
                    data-testid="fab-assign-override"
                  />
                  I confirm — override the sqft cap
                </label>
              </div>
            </div>
          )}

          {/* Advance (optional but common) */}
          <div className="bg-slate-900/60 border border-amber-500/30 rounded-sm p-2 space-y-2">
            <div className="text-[10px] font-mono-num tracking-widest text-amber-300">
              // ADVANCE TO FABRICATOR (optional)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT (₹)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={aAdvance} onChange={(e) => setAAdvance(e.target.value)}
                  className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num"
                  data-testid="fab-assign-adv-amount"
                />
              </div>
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
                <Select value={aAdvMode} onValueChange={setAAdvMode}>
                  <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1" data-testid="fab-assign-adv-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-600 text-white">
                    {modes.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DATE</Label>
                <input
                  type="date"
                  value={aAdvDate} onChange={(e) => setAAdvDate(e.target.value)}
                  max={istToday()}
                  className="bg-slate-800/60 border border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num px-2 w-full"
                  data-testid="fab-assign-adv-date"
                />
              </div>
            </div>
            <Input
              value={aAdvNote} onChange={(e) => setAAdvNote(e.target.value)}
              placeholder="Note (optional): Advance for gate fabrication"
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
              data-testid="fab-assign-adv-note"
            />
          </div>

          <div className="flex flex-wrap gap-2 items-center justify-between bg-slate-900/60 border border-slate-700 rounded-sm p-2 text-xs font-mono-num">
            <div className="flex flex-wrap gap-3">
              <SummaryDot label="TOTAL PAYABLE" value={fmt(jobValue)} tone="cyan" testid="fab-assign-total-payable" />
              <SummaryDot label="ADVANCE" value={fmt(Number(aAdvance) || 0)} tone="amber" testid="fab-assign-advance-preview" />
              <SummaryDot label="DUE" value={fmt(dueAfterAdv)} tone="red" testid="fab-assign-due-preview" />
            </div>
            <Button
              type="submit"
              disabled={busy || !selectedId || !(Number(aSqft) > 0) || (overRequested && !aOverride)}
              className="btn-industrial rounded-sm h-10 px-4"
              data-testid="fab-assign-submit"
            >
              <ArrowRight size={14} className="mr-1" />
              {Number(aAdvance) > 0 ? "ASSIGN & PAY ADVANCE" : "ASSIGN"}
            </Button>
          </div>
        </form>
      )}

      {/* Legacy: quick pay-advance flow for currently active fabricator */}
      {showPayForm && currentFabricator && (
        <form onSubmit={submitPayment} className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end bg-slate-800/40 rounded-sm p-3" data-testid="fab-pay-form">
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT (₹)</Label>
            <Input
              type="number" min="0" step="0.01"
              value={amt} onChange={(e) => setAmt(e.target.value)}
              autoFocus
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num"
              data-testid="fab-pay-amount"
            />
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1" data-testid="fab-pay-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-600 text-white">
                {modes.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DATE</Label>
            <input
              type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)}
              max={istToday()}
              className="bg-slate-800/60 border border-slate-600 text-white h-10 rounded-sm mt-1 font-mono-num px-2 w-full"
              data-testid="fab-pay-date"
            />
          </div>
          <div className="md:col-span-4 flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE (optional)</Label>
              <Input
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g., Second payment"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1"
                data-testid="fab-pay-note"
              />
            </div>
            <Button
              type="submit" disabled={busy || !(Number(amt) > 0)}
              className="btn-industrial rounded-sm h-10 px-5"
              data-testid="fab-pay-submit"
            >
              <Plus size={14} className="mr-1" /> LOG PAYMENT
            </Button>
          </div>
        </form>
      )}

      {/* Payment ledger */}
      {payments.length > 0 && (
        <div className="mt-3 rounded-sm border border-slate-700 overflow-hidden" data-testid="fab-ledger">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400 bg-slate-800/50 px-3 py-2 flex items-center gap-1">
            <ClockClockwise size={12} /> ADVANCE LEDGER ({payments.length})
          </div>
          <div className="divide-y divide-slate-800">
            {payments.map((p) => (
              <div key={p.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-num text-emerald-400 font-bold">+{fmt(p.amount)}</span>
                    <span className="text-[10px] font-mono-num tracking-widest text-slate-500">{(p.mode || "").toUpperCase()}</span>
                    <span className="text-[10px] font-mono-num text-slate-500">{p.paid_on || (p.created_at || "").slice(0, 10)}</span>
                  </div>
                  {p.note && <div className="text-slate-400 text-xs truncate mt-0.5">{p.note}</div>}
                </div>
                <button
                  onClick={() => removePayment(p.id)}
                  className="text-slate-500 hover:text-red-400 p-1"
                  data-testid={`fab-ledger-del-${p.id}`}
                  title="Delete this entry"
                >
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function StatChip({ label, value, tone, testid }) {
  const toneCls = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    emerald: "text-emerald-400",
    red: "text-red-400",
    slate: "text-slate-300",
  }[tone] || "text-slate-100";
  return (
    <div data-testid={testid}>
      <div className="text-[9px] font-mono-num tracking-widest text-slate-500">{label}</div>
      <div className={"font-mono-num font-bold text-sm " + toneCls}>{value}</div>
    </div>
  );
}


function SummaryDot({ label, value, tone, testid }) {
  const toneCls = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    emerald: "text-emerald-400",
    red: "text-red-400",
  }[tone] || "text-slate-100";
  return (
    <div className="flex items-baseline gap-1" data-testid={testid}>
      <span className="text-[9px] tracking-widest text-slate-500">{label}</span>
      <span className={"font-bold " + toneCls}>{value}</span>
    </div>
  );
}


function ActiveAssignmentCard({ a, fmt, sqft, testid, onPayAdvance, onUnassign }) {
  const isLegacy = !!a.is_legacy;
  const jv = a.job_value;
  const paid = Number(a.paid || 0);
  const due = a.due;
  return (
    <div
      className="bg-cyan-500/5 border border-cyan-500/30 rounded-sm p-3 flex flex-wrap items-start gap-3"
      data-testid={testid}
    >
      <div className="min-w-0 flex-1">
        <div className="font-heading font-black text-cyan-200 truncate">
          {a.fabricator_name || "—"}
          {isLegacy && (
            <span className="ml-2 text-[9px] font-mono-num tracking-widest bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-sm">
              LEGACY · NOT SET
            </span>
          )}
        </div>
        <div className="text-cyan-300 font-mono-num text-xs truncate">{a.fabricator_email}</div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
          <MetricBox label="ASSIGNED" value={a.assigned_sqft == null ? "NOT SET" : sqft(a.assigned_sqft)} />
          <MetricBox label="RATE" value={a.labour_rate_per_sqft == null ? "NOT SET" : `₹${a.labour_rate_per_sqft}/sqft`} />
          <MetricBox label="JOB VALUE" value={jv == null ? "NOT SET" : fmt(jv)} tone="cyan" />
          <MetricBox label="PAID" value={fmt(paid)} tone="emerald" />
        </div>
        {due != null && (
          <div className="mt-2">
            <MetricBox
              label="FABRICATOR DUE"
              value={fmt(due)}
              tone={due > 0 ? "red" : "emerald"}
            />
          </div>
        )}
        {a.deadline_date && (
          <div className="mt-1 text-[10px] font-mono-num text-amber-300">
            DEADLINE: {a.deadline_date}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {onPayAdvance && (
          <Button
            onClick={onPayAdvance}
            variant="outline"
            size="sm"
            className="rounded-sm border-amber-500/60 text-amber-300 hover:bg-amber-500/10 text-[10px] font-mono-num tracking-widest h-8"
            data-testid={`${testid}-pay-btn`}
          >
            <CurrencyInr size={12} weight="bold" className="mr-1" />
            PAY
          </Button>
        )}
        {onUnassign && (
          <Button
            onClick={onUnassign}
            variant="outline"
            size="sm"
            className="rounded-sm border-red-500/60 text-red-300 hover:bg-red-500/10 text-[10px] font-mono-num tracking-widest h-8"
            data-testid={`${testid}-unassign-btn`}
          >
            REMOVE
          </Button>
        )}
      </div>
    </div>
  );
}


function MetricBox({ label, value, tone }) {
  const toneCls = {
    cyan: "text-cyan-300",
    amber: "text-amber-300",
    emerald: "text-emerald-400",
    red: "text-red-400",
  }[tone] || "text-slate-200";
  return (
    <div className="min-w-0">
      <div className="text-[9px] font-mono-num tracking-widest text-slate-500">{label}</div>
      <div className={"font-mono-num font-bold text-sm " + toneCls}>{value}</div>
    </div>
  );
}
